import React, { useCallback, useContext, useMemo, useState } from 'react';
import { CloudUpload as CloudUploadIcon, CreateNewFolder as CreateNewFolderIcon } from '@mui/icons-material';
import { Box, Button, Container, styled, Typography } from '@mui/material';
import { ADD_NEW_DOCUMENT, ADD_SUBFOLDER_LABEL, UPLOAD_DOCUMENT } from 'common/constants/documents';
import { DocumentDialog, RequestDocumentDialog } from 'components/Documents';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import AddFolderDialog from 'pages/Documents/components/AddFolderDialog/AddFolderDialog';
import { CurrentRequestedDocumentTableFooterProps } from './types';

const PREFIX = 'CurrentRequestedDocumentTableFooter';

const classes = {
  footer: `${PREFIX}-footer`,
  footerText: `${PREFIX}-footerText`,
  buttonContainer: `${PREFIX}-buttonContainer`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.footer}`]: {
    alignItems: 'center',
    backgroundColor: theme?.palette?.grey?.[200],
    bottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    left: 0,
    position: 'fixed',
    width: '100dvw',
    zIndex: 1000,
  },

  [`& .${classes.footerText}`]: {
    textAlign: 'center',
  },

  [`& .${classes.buttonContainer}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
}));

const CurrentRequestedDocumentTableFooter = ({ currentDocuments }: CurrentRequestedDocumentTableFooterProps) => {
  const { refreshDocuments, currentMeasurementDateId, currentCompanyMeasurementDateId, permissions } = useContext(
    InformationDashboard409AContext
  );

  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [openNewDocumentDialog, setOpenNewDocumentDialog] = useState(false);
  const [openRequestNewDocument, setOpenRequestNewDocument] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const handleClose = useCallback(() => {
    refreshDocuments?.();
    setOpenNewDocumentDialog(false);
    setOpenFolderDialog(false);
    setOpenRequestNewDocument(false);
  }, [refreshDocuments]);

  const currentFolders = useMemo(
    () => currentDocuments?.folders?.filter(folder => folder.parent_folder === null),
    [currentDocuments]
  );

  const renderDocumentDialog = useCallback(
    () => (
      <DocumentDialog
        open={openNewDocumentDialog}
        onClose={handleClose}
        title={ADD_NEW_DOCUMENT}
        addSelectComponent={false}
        droppedFiles={droppedFiles}
        setDroppedFiles={setDroppedFiles}
        selectedMeasurementDateId={Number(currentMeasurementDateId)}
        customButtonLabel="Done"
        fromReferenceWidget={false}
      />
    ),
    [openNewDocumentDialog, handleClose, droppedFiles, currentMeasurementDateId]
  );

  const renderAddFolderDialog = useCallback(
    () => (
      <AddFolderDialog
        open={openFolderDialog}
        onClose={handleClose}
        title={ADD_SUBFOLDER_LABEL}
        currentFolders={currentFolders}
        currentDocuments={currentDocuments}
      />
    ),
    [openFolderDialog, handleClose, currentFolders, currentDocuments]
  );

  const renderRequestDocumentDialog = useCallback(
    () => (
      <RequestDocumentDialog
        open={openRequestNewDocument}
        onClose={handleClose}
        currentDocuments={currentDocuments}
        companyMeasurementDateId={Number(currentCompanyMeasurementDateId)}
      />
    ),
    [openRequestNewDocument, handleClose, currentDocuments, currentCompanyMeasurementDateId]
  );

  const handleOpenFolderDialog = useCallback(() => setOpenFolderDialog(true), []);
  const handleOpenNewDocumentDialog = useCallback(() => setOpenNewDocumentDialog(true), []);

  return (
    <Root>
      {renderDocumentDialog()}
      {renderAddFolderDialog()}
      {renderRequestDocumentDialog()}
      <Box component="footer" className={classes.footer}>
        <Container maxWidth="lg" className={classes.buttonContainer}>
          {permissions?.canUseInfoRequestLedger && (
            <>
              <Button startIcon={<CreateNewFolderIcon />} color="primary" onClick={handleOpenFolderDialog}>
                {ADD_SUBFOLDER_LABEL}
              </Button>
              <Button startIcon={<CloudUploadIcon />} color="primary" onClick={handleOpenNewDocumentDialog}>
                {UPLOAD_DOCUMENT}
              </Button>
            </>
          )}
          <Typography variant="body2" color="textSecondary" className={classes.footerText}>
            {currentDocuments?.files?.length} Document(s) | {currentDocuments?.folders?.length} Subfolder(s)
          </Typography>
        </Container>
      </Box>
    </Root>
  );
};

export default CurrentRequestedDocumentTableFooter;
